import React from 'react';

import { PlusIcon, BookMarkedIcon } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { DetailsPanelToggle } from '@/components/atoms/DetailsPanelToggle/DetailsPanelToggle';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { ReadOnlyBanner } from '@/components/atoms/ReadOnlyBanner/ReadOnlyBanner';
import {
  PageSubHeader,
  PageSubHeaderActions,
  PageSubHeaderBreadcrumbs,
} from '@/components/organisms/PageSubHeader';
import { Sidebar } from '@/components/organisms/Sidebar/Sidebar';
import { SidebarLayout } from '@/components/organisms/Sidebar/SidebarLayout';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { EmptySkeleton } from '@/components/ui/custom/EmptySkeleton';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import useCollections from '@/hooks/useCollections';
import { useDirtyFormBlocker } from '@/hooks/useDirtyFormBlocker';
import { useEmptyStateView } from '@/hooks/useEmptyStateView';
import { useSetDefaultSidebar } from '@/hooks/useSetDefaultSidebar';
import { useSidebar } from '@/hooks/useSidebar';
import { useBilling } from '@/modules/billing/hooks/useBilling';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectCollectionId } from '@/redux/session/selectors';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { TABLE_LAYOUT_FILL_CLASSES } from '@/util/constants';

import { CollectionDetails } from './RightPanel/CollectionDetails';
import { CollectionForm } from './RightPanel/CollectionForm';
import { CollectionsTable } from './Tables/CollectionsTable/CollectionsTable';

const ResizableCollectionsComponent = () => {
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'collections', actions.WRITE)
  );

  const {
    collections,
    totalCount,
    listStatus,
    maxCollections,
    maxCollectionsReached,
    totalFragmentsReached,
  } = useCollections();
  const { billingState } = useBilling();

  useSetDefaultSidebar('details');
  const { isOpen, open: openPanel, close: closePanel } = useSidebar();

  const activeCollectionId = useSelector(selectCollectionId);
  const isDetailsSidebarOpen = isOpen('details');
  const isCreateCollectionSidebarOpen = isOpen('create_collection');

  const { t } = useTranslation();

  const detailsTooltipContent = isDetailsSidebarOpen
    ? t('common.hide_details')
    : t('common.show_details');

  const { showEmpty, showEmptySkeleton, showList } = useEmptyStateView({
    scope: 'collections',
    isLoading: listStatus === 'pending',
    isEmpty: listStatus === 'success' && collections?.length === 0,
  });

  // Block the user from navigating to other routes if any form is dirty
  useDirtyFormBlocker();
  const getTooltipTitle = () => {
    if (!canWrite) {
      return t('ai_agents.no_permission');
    } else if (maxCollectionsReached) {
      return t('limits.reached', { 0: t('common.collections'), 1: billingState?.plan_name });
    } else if (totalFragmentsReached) {
      return t('limits.collections_fragments_reached');
    }

    return <Trans i18nKey="limits.create_resource" values={{ 0: totalCount, 1: maxCollections }} />;
  };

  return (
    <div className="flex h-full w-full flex-col">
      <div className="min-h-0 flex-1">
        <SidebarLayout
          autoSaveId="collections-sidebar"
          sidebar={
            <Sidebar
              when={['details', 'create_collection']}
              title={t(
                isCreateCollectionSidebarOpen
                  ? 'collections.create_collection'
                  : 'collections.details'
              )}
            >
              {isCreateCollectionSidebarOpen && <CollectionForm />}
              {isDetailsSidebarOpen && <CollectionDetails />}
            </Sidebar>
          }
        >
          <PageSubHeader
            breadcrumbs={
              <PageSubHeaderBreadcrumbs>
                <Breadcrumb>
                  <BreadcrumbList>
                    <BreadcrumbItem>
                      <BreadcrumbPage>
                        <div className="flex h-8 w-8 items-center justify-center">
                          <BookMarkedIcon size={16} className="text-foreground-muted" />
                        </div>
                        <div className="mr-4 ml-2 h-4 w-px bg-border" />
                        <span className="text-sm leading-5 font-normal">
                          {t('common.collections')}
                        </span>
                      </BreadcrumbPage>
                    </BreadcrumbItem>
                  </BreadcrumbList>
                </Breadcrumb>
              </PageSubHeaderBreadcrumbs>
            }
            actions={
              <PageSubHeaderActions>
                <div className="flex items-center gap-2">
                  <DetailsPanelToggle
                    pressed={isDetailsSidebarOpen}
                    onPressedChange={(pressed) => (pressed ? openPanel('details') : closePanel())}
                    tooltip={detailsTooltipContent}
                  />
                  {listStatus === 'success' && collections?.length > 0 && (
                    <InfoTooltip content={getTooltipTitle()} size="large">
                      <span>
                        <Button
                          onClick={() => {
                            openPanel('create_collection');
                          }}
                          size="sm"
                          disabled={!canWrite || maxCollectionsReached || totalFragmentsReached}
                        >
                          <PlusIcon size={16} />
                          {t('common.create')}
                        </Button>
                      </span>
                    </InfoTooltip>
                  )}
                </div>
              </PageSubHeaderActions>
            }
          />

          {!canWrite && <ReadOnlyBanner />}

          <div className={TABLE_LAYOUT_FILL_CLASSES}>
            {showEmpty && (
              <Empty>
                <EmptyHeader>
                  <EmptyMedia>
                    <img src="/assets/empty-states/knowledge-base.webp" alt="" />
                  </EmptyMedia>
                  <EmptyTitle>{t('collections.empty_page.title')}</EmptyTitle>
                  <EmptyDescription>{t('collections.empty_page.description')}</EmptyDescription>
                </EmptyHeader>
                {canWrite && (
                  <EmptyContent>
                    <Button
                      size="sm"
                      onClick={() => {
                        openPanel('create_collection');
                      }}
                    >
                      <PlusIcon size={16} />
                      {t('common.create')}
                    </Button>
                  </EmptyContent>
                )}
              </Empty>
            )}

            {showEmptySkeleton && <EmptySkeleton descriptionLines={3} />}

            {showList && (
              <div className="flex h-full flex-col">
                <CollectionsTable
                  activeRowId={(isCreateCollectionSidebarOpen && activeCollectionId) || undefined}
                />
              </div>
            )}
          </div>
        </SidebarLayout>
      </div>
    </div>
  );
};

export const ResizableCollections = React.memo(ResizableCollectionsComponent);
